import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, PenTool, Mic, Brain, TrendingUp, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { StandardsBadges } from '@/components/StandardsBadges';
import CanDoReference from '@/components/CanDoReference';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GradingTab = ({ pendingGrading, setPendingGrading, initialGraded, user }) => {
  const [gradedRecent, setGradedRecent] = useState(initialGraded || []);
  const [gradingFeedback, setGradingFeedback] = useState({});
  const [gradingFilter, setGradingFilter] = useState('all');
  const [expandedRubric, setExpandedRubric] = useState(null);

  const gradeResponse = async (examSessionId, questionId, points) => {
    try {
      const fbKey = `${examSessionId}_${questionId}`;
      const feedback = gradingFeedback[fbKey] || '';
      await axios.post(`${API}/responses/grade`, {
        exam_session_id: examSessionId,
        question_id: questionId,
        points_earned: points,
        graded_by: user?.name || 'Teacher',
        feedback: feedback,
      });
      
      toast.success(`Scored ${points} point${points !== 1 ? 's' : ''}!`);
      
      const graded_item = pendingGrading.find(p => p.exam_session_id === examSessionId && p.question_id === questionId);
      setPendingGrading(prev => 
        prev.filter(p => !(p.exam_session_id === examSessionId && p.question_id === questionId))
      );
      if (graded_item) {
        setGradedRecent(prev => [{...graded_item, points_earned: points, graded_by: user?.name, teacher_feedback: feedback, graded_at: new Date().toISOString()}, ...prev].slice(0, 20));
      }
      setGradingFeedback(prev => { const n = {...prev}; delete n[fbKey]; return n; });
    } catch (error) {
      toast.error('Failed to grade response');
    }
  };

  const filteredPending = pendingGrading.filter(p => {
    if (gradingFilter === 'all') return true;
    if (gradingFilter === 'custom') return p.is_custom;
    return p.question?.modality === gradingFilter;
  });

  return (
    <>
      {/* How-to Guide Banner */}
      <div className="bg-blue-50 border-2 border-blue-200 p-4 mb-4" data-testid="grading-guide">
        <h4 className="font-bold text-[#002FA7] mb-2">How Grading Works</h4>
        <div className="text-sm text-gray-700 space-y-1">
          <p><strong>Writing</strong> and <strong>Speaking</strong> responses require manual teacher scoring using proficiency rubrics (0-6 scale).</p>
          <p>When a student completes an exam, their multiple-choice answers are scored automatically, but open-ended responses appear here for you to review.</p>
          <p><strong>Steps:</strong> 1) Read the student's response below &rarr; 2) Open the proficiency rubric for guidance &rarr; 3) Optionally write feedback &rarr; 4) Click a score button (0-6).</p>
          <p className="text-xs text-gray-500 mt-1">Custom teacher-made questions (marked with a badge) also appear here when they need grading.</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm font-semibold text-gray-500">Filter:</span>
        {[
          { key: 'all', label: 'All' },
          { key: 'writing', label: 'Writing' },
          { key: 'speaking', label: 'Speaking' },
          { key: 'custom', label: 'Custom Questions' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setGradingFilter(f.key)}
            className={`text-sm px-3 py-1 border ${gradingFilter === f.key ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
            data-testid={`grading-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-sm text-gray-400">
          {filteredPending.length} pending
        </span>
      </div>

      <div className="border-2 border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold">Responses Requiring Manual Grading</h3>
          <p className="text-sm text-gray-500">Speaking and Writing responses need teacher scoring using proficiency rubrics</p>
        </div>
        
        {filteredPending.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-300" />
            <h4 className="font-semibold text-lg mb-2">All caught up!</h4>
            <p>No responses pending grading{gradingFilter !== 'all' ? ` for ${gradingFilter}` : ''}.</p>
            <p className="text-xs text-gray-400 mt-2">Responses appear here when students submit Writing or Speaking answers.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredPending.map((item, idx) => {
              const fbKey = `${item.exam_session_id}_${item.question_id}`;
              return (
              <div key={idx} className="p-6" data-testid={`grading-item-${idx}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center ${
                    item.question?.modality === 'speaking' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {item.question?.modality === 'speaking' ? <Mic size={20} /> : <PenTool size={20} />}
                  </div>
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-bold text-base text-gray-900">
                        {item.student_name || 'Unknown Student'}
                      </span>
                      {item.class_name && (
                        <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 font-medium border border-blue-200" data-testid={`class-badge-${idx}`}>
                          {item.class_name}
                        </span>
                      )}
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500">Grade {item.grade_level}</span>
                      {item.school_name && (
                        <span className="text-xs text-gray-400 ml-auto" data-testid={`school-badge-${idx}`}>
                          {item.school_name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-sm">
                        {item.question?.modality === 'speaking' ? 'Speaking' : 'Writing'} Response
                      </span>
                      {item.is_custom && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 font-medium">Custom Question</span>
                      )}
                      {item.wida_standard && (
                        <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 font-medium border border-indigo-200" data-testid={`wida-standard-badge-${idx}`} title={item.wida_mpi || ''}>
                          WIDA Standard {item.wida_eld_level ? `(ELD ${item.wida_eld_level})` : ''}
                        </span>
                      )}
                      <StandardsBadges eldStandard={item.question?.eld_standard} klu={item.question?.key_language_use} size="xs" />
                    </div>

                    {/* Question Text */}
                    <div className="text-sm text-gray-600 mb-3">
                      <span className="font-medium">Prompt: </span>{item.question?.question_text}
                    </div>

                    {/* Stimulus */}
                    {item.question?.stimulus && (
                      <div className="text-xs text-gray-500 bg-gray-50 p-2 mb-3 border-l-2 border-gray-300">
                        {item.question.stimulus.substring(0, 200)}{item.question.stimulus.length > 200 ? '...' : ''}
                      </div>
                    )}

                    {/* Student Response */}
                    <div className="bg-gray-50 border-2 border-gray-200 p-4 mb-4" data-testid={`student-response-${idx}`}>
                      <div className="text-xs font-semibold text-gray-500 mb-2 uppercase">Student's Response:</div>
                      <p className="text-sm whitespace-pre-wrap">{item.student_answer || <span className="italic text-gray-400">No response provided</span>}</p>
                    </div>

                    {/* Automated Rubric Suggestion Panel */}
                    {item.ai_rubric_scores && (
                      <div className="bg-indigo-50 border-2 border-indigo-200 p-4 mb-4" data-testid={`ai-rubric-panel-${idx}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <Brain size={16} className="text-indigo-600" />
                          <span className="font-bold text-sm text-indigo-700">Automated Rubric Suggestion</span>
                          <span className="text-xs text-indigo-400 ml-auto">Suggested score: {item.ai_rubric_scores.overall_level}/6</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          {[
                            { key: 'linguistic_complexity', label: 'Linguistic Complexity', desc: 'Sentence structure variety' },
                            { key: 'vocabulary_usage', label: 'Vocabulary', desc: 'Word choice precision' },
                            { key: 'language_control', label: 'Language Control', desc: 'Grammar & conventions' },
                            ...(item.ai_rubric_scores.organization !== undefined ? [{ key: 'organization', label: 'Organization', desc: 'Text structure' }] : []),
                          ].map(dim => {
                            const val = item.ai_rubric_scores[dim.key];
                            const colorClass = val >= 5 ? 'bg-green-500' : val >= 3 ? 'bg-yellow-500' : 'bg-red-500';
                            return (
                              <div key={dim.key} className="bg-white border border-indigo-100 p-2.5" data-testid={`ai-dim-${dim.key}-${idx}`}>
                                <div className="text-xs text-gray-500 mb-1">{dim.label}</div>
                                <div className="flex items-center gap-2">
                                  <div className={`w-7 h-7 ${colorClass} text-white flex items-center justify-center font-bold text-sm`}>{val}</div>
                                  <div className="flex-1 bg-gray-100 h-1.5">
                                    <div className={`h-full ${colorClass}`} style={{ width: `${(val / 6) * 100}%` }} />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {item.ai_rubric_scores.feedback && (
                          <div className="text-sm text-indigo-800 mb-2">
                            <span className="font-semibold">Feedback: </span>{item.ai_rubric_scores.feedback}
                          </div>
                        )}
                        <div className="flex gap-4 text-xs text-indigo-600">
                          {item.ai_rubric_scores.strengths && (
                            <div className="flex-1"><span className="font-semibold">Strengths: </span>{item.ai_rubric_scores.strengths}</div>
                          )}
                          {item.ai_rubric_scores.next_steps && (
                            <div className="flex-1"><span className="font-semibold">Next Steps: </span>{item.ai_rubric_scores.next_steps}</div>
                          )}
                        </div>
                        <div className="text-xs text-indigo-400 mt-2 flex items-center gap-1">
                          <TrendingUp size={12} /> System suggestion — teacher has final scoring authority
                        </div>
                      </div>
                    )}

                    {/* Can-Do Reference for context while grading */}
                    {item.question?.modality && item.grade_level && (
                      <div className="mb-4">
                        <CanDoReference modality={item.question.modality} level={Math.max(1, Math.min(6, item.wida_eld_level || 3))} compact={true} />
                      </div>
                    )}

                    {/* WIDA Rubric */}
                    <div className="mb-4">
                      <button
                        onClick={() => setExpandedRubric(expandedRubric === idx ? null : idx)}
                        className="text-sm text-[#002FA7] font-medium cursor-pointer hover:underline"
                        data-testid={`rubric-toggle-${idx}`}
                      >
                        {expandedRubric === idx ? 'Hide' : 'View'} WIDA {item.question?.modality === 'speaking' ? 'Speaking' : 'Writing'} Rubric
                      </button>
                      {expandedRubric === idx && (
                        <div className="mt-2 bg-yellow-50 border-2 border-yellow-200 p-4 text-xs" data-testid={`rubric-content-${idx}`}>
                          {item.question?.modality === 'writing' ? (
                            <div className="space-y-2">
                              <p className="font-bold text-sm mb-2">WIDA Writing Rubric (Score 0-6)</p>
                              <div><strong>6 - Reaching:</strong> Writing closely approaches grade-level expectations. Complex sentence structures, academic vocabulary, minimal errors.</div>
                              <div><strong>5 - Bridging:</strong> Uses a variety of sentence structures, expanding use of academic vocabulary. Few errors that don't impede communication.</div>
                              <div><strong>4 - Expanding:</strong> Short, expanded, and some complex sentences. Specific and some technical language. Errors don't impede comprehension.</div>
                              <div><strong>3 - Developing:</strong> Simple and expanded sentences. General and some specific language. Errors may impede meaning sometimes.</div>
                              <div><strong>2 - Emerging:</strong> Single words, phrases, or chunks of language. General language. Frequent errors often impede meaning.</div>
                              <div><strong>1 - Entering:</strong> Copied or adapted single words. Pictorial or graphic representation. Non-standard or first-language use.</div>
                              <div><strong>0:</strong> No response, off-topic, or illegible.</div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <p className="font-bold text-sm mb-2">WIDA Speaking Rubric (Score 0-6)</p>
                              <div><strong>6 - Reaching:</strong> Oral language approaches grade-level expectations. Complex grammar, rich academic vocabulary, natural fluency.</div>
                              <div><strong>5 - Bridging:</strong> Extended oral discourse with a variety of sentence structures. Technical language used appropriately.</div>
                              <div><strong>4 - Expanding:</strong> Short, expanded, and some complex sentences. Specific and some technical vocabulary. Generally fluent.</div>
                              <div><strong>3 - Developing:</strong> Simple and expanded sentences. General and some specific vocabulary. Some hesitation.</div>
                              <div><strong>2 - Emerging:</strong> Phrases and short sentences. General language with repetition. Frequent hesitation.</div>
                              <div><strong>1 - Entering:</strong> Single words or memorized chunks. Non-verbal pointing or gesturing. First language use.</div>
                              <div><strong>0:</strong> No response or completely off-topic.</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Feedback Field */}
                    <div className="mb-4">
                      <label className="text-xs font-semibold text-gray-500 block mb-1">Teacher Feedback (optional)</label>
                      <textarea
                        value={gradingFeedback[fbKey] || ''}
                        onChange={(e) => setGradingFeedback(prev => ({...prev, [fbKey]: e.target.value}))}
                        className="w-full border border-gray-300 px-3 py-2 text-sm min-h-[60px]"
                        placeholder="Write feedback for the student (e.g., 'Good use of descriptive language. Try to add more detail about...')"
                        data-testid={`feedback-input-${idx}`}
                      />
                    </div>

                    {/* Score Buttons */}
                    <div className="flex items-center gap-2 flex-wrap" data-testid={`score-buttons-${idx}`}>
                      <span className="text-sm text-gray-500 mr-1 font-medium">Proficiency Score:</span>
                      {[0, 1, 2, 3, 4, 5, 6].slice(0, (item.question?.max_points || 6) + 1).map(score => (
                        <Button
                          key={score}
                          variant="outline"
                          size="sm"
                          onClick={() => gradeResponse(item.exam_session_id, item.question_id, score)}
                          className={`w-11 h-11 p-0 btn-sharp font-bold text-base ${
                            score === 0 ? 'hover:bg-red-50 hover:border-red-400 hover:text-red-600' :
                            score <= 2 ? 'hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600' :
                            score <= 4 ? 'hover:bg-yellow-50 hover:border-yellow-500 hover:text-yellow-700' :
                            'hover:bg-green-50 hover:border-green-500 hover:text-green-700'
                          }`}
                          data-testid={`score-${score}-btn-${idx}`}
                        >
                          {score}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );})}
          </div>
        )}
      </div>

      {/* Recently Graded Section */}
      {gradedRecent.length > 0 && (
        <div className="border-2 border-gray-200 mt-6" data-testid="recently-graded">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-bold text-sm">Recently Graded ({gradedRecent.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {gradedRecent.slice(0, 10).map((item, idx) => (
              <div key={idx} className="p-4 flex items-center gap-4" data-testid={`graded-item-${idx}`}>
                <div className={`w-10 h-10 flex items-center justify-center font-bold text-lg ${
                  item.points_earned >= 5 ? 'bg-green-100 text-green-700' :
                  item.points_earned >= 3 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {item.points_earned}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    {item.student_name} &mdash; {item.question?.modality === 'speaking' ? 'Speaking' : 'Writing'}
                    {item.class_name && <span className="text-xs text-blue-600 ml-2">({item.class_name})</span>}
                  </div>
                  <div className="text-xs text-gray-500">{item.question?.question_text?.substring(0, 80)}...</div>
                  {item.teacher_feedback && (
                    <div className="text-xs text-blue-600 mt-1">Feedback: {item.teacher_feedback.substring(0, 100)}{item.teacher_feedback.length > 100 ? '...' : ''}</div>
                  )}
                </div>
                <div className="text-xs text-gray-400">{item.graded_by} &bull; {item.graded_at ? new Date(item.graded_at).toLocaleDateString() : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default GradingTab;
